import React from 'react';
import { ParallaxSection } from '../components/ParallaxSection';
import { SectionHeading } from '../components/SectionHeading';
import { motion } from 'framer-motion';
const milestones = [{
  year: '2006',
  title: 'Foundation',
  desc: 'Sinclair founded with a vision in residential 2–3 story homes.'
}, {
  year: '2012',
  title: 'Expansion',
  desc: 'Opened services for commercial renovation and building.'
}, {
  year: '2019',
  title: 'Innovation',
  desc: 'Adopted new cutting-edge processes for optimizing schedules and material delivery.'
}, {
  year: '2022',
  title: 'Multipurpose Build',
  desc: 'Built a 10,500 sq ft multipurpose building with steel structural and CastCrete roof.'
}, {
  year: '2023',
  title: 'Flex Space',
  desc: 'Completed a 2-unit, 10,400 sq ft flex space for versatile commercial use.'
}, {
  year: '2024',
  title: '10-Plex Commercial',
  desc: 'Built the first 10-plex professional business commercial project, now home to 10 unique businesses.'
}];
export function AboutPage() {
  return <div className="bg-white" data-id="element-174" data-mp="2jvtv1y2jevek">
      <ParallaxSection backgroundImage="https://images.unsplash.com/photo-1497366216548-37526070297c?w=1920&q=80" height="h-[50vh]" data-id="element-175" data-mp="2jw3ojvmsjkp0">
        <h1 className="text-5xl md:text-7xl font-bold font-serif text-white text-center" data-id="element-176" data-mp="2aucekel7wrpc">
          About Sinclair
        </h1>
        <p className="text-xl md:text-2xl text-[#C5A55A] text-center mt-4 italic font-serif tracking-wide" data-id="element-177" data-mp="2yl9k99mf6ss5">
          "My name is all I have"
        </p>
      </ParallaxSection>

      {/* Story Section */}
      <section className="py-20 px-4 md:px-8" data-id="element-178" data-mp="2otlwbip2ntvf">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-179" data-mp="2tti1hl9i9rck">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-180" data-mp="2tr5dorh2gmrl">
            <img src="https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=800&q=80" alt="Construction site" className="w-full h-auto rounded-sm shadow-2xl" data-id="element-181" data-mp="3r7q2tqm1pcje" />
          </motion.div>
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-182" data-mp="3hav3x6lv1a4l">
            <h2 className="text-3xl md:text-4xl font-bold font-serif mb-6 text-black" data-id="element-183" data-mp="2yiskjnhu3fqn">
              Our Story
            </h2>
            <div className="w-20 h-1 bg-[#C5A55A] mb-8" data-id="element-184" data-mp="2tieluw24hi5a" />
            <p className="text-gray-600 mb-6 leading-relaxed" data-id="element-185" data-mp="2pgt1kpn9e1l2">
              For decades Sinclair Development has had hands-on experience
              building commercial and residential projects across Southwest
              Florida. Our roots are based on practical building knowledge,
              quality construction methods and attention to details.
            </p>
            <p className="text-gray-600 mb-6 leading-relaxed" data-id="element-186" data-mp="1dy5i9eeuh9ab">
              Our goal is to provide well built spaces for our clients' needs,
              whether it's a commercial build out, a specialty space, or
              coordinating trades for a custom project. Our method of providing
              solid construction while thinking about how we can best use design
              for our clients' functional needs, while also considering the
              long-term value and cost.
            </p>
            <p className="text-gray-600 leading-relaxed" data-id="element-187" data-mp="2ktcuz3fsim1">
              Sinclair Development doesn't want to be all things to all people;
              we want to provide responsible building services, clear
              communication and complete our projects properly.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission/Vision/Values */}
      <section className="py-20 px-4 md:px-8 bg-gray-50" data-id="element-188" data-mp="255dvrznetgvt">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-189" data-mp="2izx1ajynh0ic">
          {[{
          title: 'Our Mission',
          text: 'To see what a property can become, then bring together the planning, design, construction and management to get it there.'
        }, {
          title: 'Our Vision',
          text: 'To be the development partner Southwest Florida turns to for land, commercial, residential and redevelopment opportunities.'
        }, {
          title: 'Our Values',
          text: 'Responsible building. Clear communication. Projects completed properly. Long-term value over shortcuts.'
        }].map((item, index) => <div key={index} className="bg-white p-10 shadow-lg border-t-4 border-[#C5A55A]" data-id="element-190" data-mp="12mq0powfoy37" data-mpi={index}>
              <h3 className="text-2xl font-bold font-serif mb-4 text-black" data-id="element-191" data-mp="3j7lc1wxd7j09">
                {item.title}
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-192" data-mp="35evp9qa0ia23">{item.text}</p>
            </div>)}
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 px-4 md:px-8 bg-white" data-id="element-193" data-mp="2vhhcq1r7m5f2">
        <div className="max-w-4xl mx-auto" data-id="element-194" data-mp="2vfwh08t5sgfm">
          <SectionHeading title="Our History" centered data-id="element-195" data-mp="30gapfryo9se6" />

          <div className="relative border-l-2 border-gray-200 ml-4 md:ml-0 md:pl-0 space-y-12" data-id="element-196" data-mp="30hvjzhkzlbc1">
            {milestones.map((item, index) => <div key={index} className="relative flex flex-col md:flex-row md:items-center gap-6 md:gap-12 pl-8 md:pl-0" data-id="element-197" data-mp="2qi4r1kc2r4e2" data-mpi={index}>
                {/* Dot */}
                <div className="absolute left-[-9px] md:left-1/2 md:-ml-[9px] w-4 h-4 rounded-full bg-[#C5A55A] border-4 border-white shadow-sm z-10" data-id="element-198" data-mp="oqro30to36kn" />

                {/* Content */}
                <div className={`md:w-1/2 ${index % 2 === 0 ? 'md:text-right md:pr-12' : 'md:ml-auto md:pl-12'}`} data-id="element-199" data-mp="1sovbcjeuqvad">
                  <span className="text-[#C5A55A] font-bold text-xl block mb-2" data-id="element-200" data-mp="1oo30jlqc0ibz">
                    {item.year}
                  </span>
                  <h3 className="text-xl font-bold font-serif text-black mb-2" data-id="element-201" data-mp="1jqjn2o1gk9l3">
                    {item.title}
                  </h3>
                  <p className="text-gray-600" data-id="element-202" data-mp="2ccm6hulwsj0w">{item.desc}</p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      <ParallaxSection backgroundImage="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920&q=80" height="h-[40vh]" data-id="element-203" data-mp="27jt4hehtrosi">
        <div className="text-center" data-id="element-204" data-mp="3038gvsjdyfe2">
          <h2 className="text-3xl md:text-5xl font-bold font-serif text-white" data-id="element-205" data-mp="3049spz8875wu">
            Excellence in Every Detail
          </h2>
        </div>
      </ParallaxSection>
    </div>;
}